import { useCallback, useEffect, useId, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { FaFont, FaLongArrowAltRight, FaRegSquare, FaSlash } from "react-icons/fa";
import type {
  U3140PdfAnnotationPresetShapeKind,
  U3140PdfAnnotationToolbarStylePreset,
  U3140PdfAnnotationToolbarPresetStyles,
} from "../../U3140PdfAnnotationsService";
import {
  U3140_RECT_DEFAULT_WIDTH_PX,
  U3140_RECT_DEFAULT_HEIGHT_PX,
  U3140_RECT_PRESET_PX_MAX,
  U3140_RECT_PRESET_PX_MIN,
  u3140ClampRectPx,
  u3140RectDisplayHeightPx,
  u3140RectDisplayWidthPx,
} from "../../U3140PdfAnnotationsService";
import { U3140_PDF_ANNOTATION_FILL_NONE, U3140_PDF_ANNOTATION_SWATCH_COLORS } from "../../U3140PdfAnnotationsPalette";
import U2000InputTextBase from "../../../u2000Core/components/formControls/input/U2000InputTextBase";

/** Aligné sur le panneau `U3140PdfAnnotationShapesMenuButton` (popover « Insérer une forme »). */
const U3142_SHAPES_MENU_POPOVER_CLASS =
  "rounded-lg border border-gray-200 bg-white p-3 shadow-xl";

const u3142PresetKindIcon = (kind: U3140PdfAnnotationPresetShapeKind) => {
  if (kind === "rect") return <FaRegSquare className="h-4 w-4 text-gray-700" aria-hidden />;
  if (kind === "line") return <FaSlash className="h-4 w-4 text-gray-700" aria-hidden />;
  if (kind === "arrow") return <FaLongArrowAltRight className="h-4 w-4 text-gray-700" aria-hidden />;
  return <FaFont className="h-4 w-4 text-gray-700" aria-hidden />;
};

const U3142_TEXT_BOX_WIDTH_MIN_NORM = 0.04;
const U3142_TEXT_BOX_WIDTH_MAX_NORM = 1;

/** Plafond produit (px) : aligné préréglage, barre d’outils et schéma API. */
const U3142_PRESET_STROKE_WIDTH_MAX_PX = 12;

function u3142ClampWidthNorm(n: number): number {
  if (!Number.isFinite(n)) {
    return U3142_TEXT_BOX_WIDTH_MIN_NORM;
  }
  return Math.min(U3142_TEXT_BOX_WIDTH_MAX_NORM, Math.max(U3142_TEXT_BOX_WIDTH_MIN_NORM, n));
}

function u3142HexForColorInput(s: string): string {
  return typeof s === "string" && s.startsWith("#") && s.length >= 4 ? s : "#000000";
}

/** Style compact pour U2000InputTextBase dans l’éditeur de préréglage (pas de spinners type="number"). */
const U3140_PRESET_U2000_NUM_COMPACT =
  "[&>div]:space-y-0 [&_input]:h-9 [&_input]:w-20 [&_input]:rounded-lg [&_input]:px-2 [&_input]:py-1.5";

interface U3140PresetNumericInputProps {
  id: string;
  value: number;
  onValueChange: (n: number) => void;
  min?: number;
  max?: number;
  allowDecimal?: boolean;
  emptyFallback?: number;
}

function u3140FormatPresetNumericDisplay(n: number, allowDecimal: boolean): string {
  return allowDecimal ? String(n) : String(Math.round(n));
}

function u3140FilterPresetNumericDraft(raw: string, allowDecimal: boolean): string {
  const normalized = raw.replace(",", ".");
  if (allowDecimal) {
    let s = normalized.replace(/[^0-9.]/g, "");
    const dot = s.indexOf(".");
    if (dot >= 0) {
      s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
    }
    return s;
  }
  return normalized.replace(/[^0-9]/g, "");
}

function u3140ParsePresetNumericDraft(
  draft: string,
  allowDecimal: boolean,
  min: number | undefined,
  max: number | undefined,
  emptyFallback: number
): number {
  const trimmed = draft.trim();
  if (trimmed === "" || trimmed === ".") {
    return emptyFallback;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) {
    return emptyFallback;
  }
  let out = allowDecimal ? n : Math.round(n);
  if (min != null) {
    out = Math.max(min, out);
  }
  if (max != null) {
    out = Math.min(max, out);
  }
  return out;
}

/**
 * Champ numérique U2000 (type texte) — sans spinners.
 * Brouillon local pendant la saisie ; validation au blur (évite de bloquer « 60 », « 2.5 », etc.).
 */
function U3140PresetNumericInput({
  id,
  value,
  onValueChange,
  min,
  max,
  allowDecimal = false,
  emptyFallback = 0,
}: U3140PresetNumericInputProps) {
  const [draft, setDraft] = useState(() => u3140FormatPresetNumericDisplay(value, allowDecimal));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(u3140FormatPresetNumericDisplay(value, allowDecimal));
    }
  }, [value, allowDecimal]);

  const commitDraft = () => {
    const out = u3140ParsePresetNumericDraft(draft, allowDecimal, min, max, emptyFallback);
    onValueChange(out);
    setDraft(u3140FormatPresetNumericDisplay(out, allowDecimal));
  };

  return (
    <div className={U3140_PRESET_U2000_NUM_COMPACT}>
      <U2000InputTextBase
        fieldName={id}
        type="string"
        value={draft}
        setValue={(v) => setDraft(u3140FilterPresetNumericDraft(String(v), allowDecimal))}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onBlur={() => {
          focusedRef.current = false;
          commitDraft();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            commitDraft();
            (e.target as HTMLInputElement).blur();
            return;
          }
          if (!allowDecimal && (e.key === "." || e.key === ",")) {
            e.preventDefault();
          }
        }}
      />
    </div>
  );
}

interface U3142ColorAndTransparentRowProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  idPrefix: string;
  i18nNamespace?: string;
}

/**
 * Remplissage (rectangle / texte) : aucun = sentinelle transparent.
 */
function U3142ColorAndTransparentRow({ label, value, onChange, idPrefix, i18nNamespace }: U3142ColorAndTransparentRowProps) {
  const { t } = useTranslation(i18nNamespace ?? "u3140");
  const isNone = value === U3140_PDF_ANNOTATION_FILL_NONE;
  return (
    <div className="space-y-1">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1.5 text-sm text-gray-800">
          <input
            type="checkbox"
            className="rounded-sm border-gray-300"
            checked={isNone}
            onChange={() => onChange(isNone ? "#FFCC00" : U3140_PDF_ANNOTATION_FILL_NONE)}
          />
          {t("lblPdfAnnotationFillNone")}
        </label>
        {!isNone && (
          <>
            <input
              type="color"
              id={`${idPrefix}-fill-c`}
              className="h-8 w-12 cursor-pointer rounded-sm border border-gray-300 p-0.5"
              value={u3142HexForColorInput(value)}
              onChange={(e) => onChange(e.target.value)}
              aria-label={label}
            />
            <div className="flex flex-wrap gap-1" role="group" aria-label={t("lblPdfAnnotationFill")}>
              {U3140_PDF_ANNOTATION_SWATCH_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="h-5 w-5 rounded-sm border border-gray-300"
                  style={{ backgroundColor: c }}
                  onClick={() => onChange(c)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface U3142StrokeColorRowProps {
  label: string;
  value: string;
  idPrefix: string;
  onChange: (v: string) => void;
}

function U3142StrokeColorRow({ label, value, idPrefix, onChange }: U3142StrokeColorRowProps) {
  return (
    <div className="space-y-1">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="color"
          id={`${idPrefix}-st-c`}
          className="h-8 w-12 cursor-pointer rounded-sm border border-gray-300 p-0.5"
          value={u3142HexForColorInput(value)}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
        />
        <div className="flex flex-wrap gap-1">
          {U3140_PDF_ANNOTATION_SWATCH_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className="h-5 w-5 rounded-sm border border-gray-300"
              style={{ backgroundColor: c }}
              onClick={() => onChange(c)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface U3142PresetFieldsetProps {
  kind: U3140PdfAnnotationPresetShapeKind;
  value: U3140PdfAnnotationToolbarStylePreset;
  onPatch: (p: Partial<U3140PdfAnnotationToolbarStylePreset>) => void;
  sectionTitle: string;
  i18nNamespace?: string;
}

/**
 * Tous les attributs `U3140PdfAnnotationToolbarStylePreset` modifiables pour un type :
 * masque seulement le remplissage sur ligne / flèche (toujours « aucun » côté modèle).
 */
function U3142PresetFieldset({ kind, value, onPatch, sectionTitle, i18nNamespace }: U3142PresetFieldsetProps) {
  const { t } = useTranslation(i18nNamespace ?? "u3140");
  const idBase = useId();

  const showFill = kind === "rect" || kind === "text";
  const showRectSizeFields = kind === "rect";
  const showTextFields = kind === "text";
  const showStrokeNoneCheckbox = kind === "rect" || kind === "text";
  const noStroke = value.strokeWidth <= 0;

  return (
    <div
      className="min-w-0 flex flex-col rounded-lg border border-gray-200 bg-white p-3"
      role="group"
      aria-label={sectionTitle}
    >
      <div className="mb-2 flex min-w-0 items-center gap-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-gray-200 bg-gray-50 text-gray-700">
          {u3142PresetKindIcon(kind)}
        </span>
        <span className="min-w-0 wrap-break-word text-left text-xs font-semibold uppercase leading-tight tracking-wide text-gray-500">
          {sectionTitle}
        </span>
      </div>
      <div className="space-y-3">
        {showFill ? (
          <U3142ColorAndTransparentRow
            idPrefix={`${idBase}-fill-${kind}`}
            label={t("lblPdfAnnotationFill")}
            value={value.fillColor}
            onChange={(v) => onPatch({ fillColor: v })}
            i18nNamespace={i18nNamespace}
          />
        ) : null}

        {!showFill && (kind === "line" || kind === "arrow") ? (
          <p className="text-xs text-gray-500">{t("msgPdfAnnotationPresetNoFillForLine")}</p>
        ) : null}

        {showStrokeNoneCheckbox ? (
          <div className="space-y-1">
            <span className="text-xs font-medium text-gray-600">{t("lblPdfAnnotationStroke")}</span>
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="rounded-sm border-gray-300"
                  checked={noStroke}
                  onChange={() => {
                    if (noStroke) {
                      onPatch({ strokeWidth: 2 });
                    } else {
                      onPatch({ strokeWidth: 0 });
                    }
                  }}
                />
                {t("lblPdfAnnotationStrokeNone")}
              </label>
              {!noStroke ? (
                <>
                  <input
                    type="color"
                    id={`${idBase}-s-${kind}-c`}
                    className="h-8 w-12 cursor-pointer rounded-sm border border-gray-300 p-0.5"
                    value={u3142HexForColorInput(value.strokeColor)}
                    onChange={(e) =>
                      onPatch({
                        strokeColor: e.target.value,
                        strokeWidth: value.strokeWidth <= 0 ? 2 : value.strokeWidth,
                      })
                    }
                    aria-label={t("lblPdfAnnotationStroke")}
                  />
                  <div
                    className="flex flex-wrap gap-1"
                    role="group"
                    aria-label={t("lblPdfAnnotationStroke")}
                  >
                    {U3140_PDF_ANNOTATION_SWATCH_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className="h-5 w-5 rounded-sm border border-gray-300"
                        style={{ backgroundColor: c }}
                        onClick={() =>
                          onPatch({
                            strokeColor: c,
                            strokeWidth: value.strokeWidth <= 0 ? 2 : value.strokeWidth,
                          })
                        }
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
            {!noStroke ? (
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600" htmlFor={`${idBase}-sw-${kind}`}>
                  {t("lblPdfAnnotationStrokeWidth")}
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <U3140PresetNumericInput
                    id={`${idBase}-sw-${kind}`}
                    value={value.strokeWidth}
                    allowDecimal
                    min={0}
                    max={U3142_PRESET_STROKE_WIDTH_MAX_PX}
                    emptyFallback={0}
                    onValueChange={(n) => onPatch({ strokeWidth: n })}
                  />
                  <span className="text-xs text-gray-500">px</span>
                  <span id={`${idBase}-sw-hint-${kind}`} className="text-xs text-gray-500">
                    ({t("lblPdfAnnotationStrokeWidthMaxHint")})
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <>
            <U3142StrokeColorRow
              idPrefix={`${idBase}-s-${kind}`}
              label={t("lblPdfAnnotationStroke")}
              value={value.strokeColor}
              onChange={(v) => onPatch({ strokeColor: v, strokeWidth: value.strokeWidth <= 0 ? 2 : value.strokeWidth })}
            />

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600" htmlFor={`${idBase}-sw-${kind}`}>
                {t("lblPdfAnnotationStrokeWidth")}
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <U3140PresetNumericInput
                  id={`${idBase}-sw-${kind}`}
                  value={value.strokeWidth}
                  allowDecimal
                  min={0}
                  max={U3142_PRESET_STROKE_WIDTH_MAX_PX}
                  emptyFallback={0}
                  onValueChange={(n) => onPatch({ strokeWidth: n })}
                />
                <span className="text-xs text-gray-500">px</span>
                <span id={`${idBase}-sw-hint-${kind}`} className="text-xs text-gray-500">
                  ({t("lblPdfAnnotationStrokeWidthMaxHint")})
                </span>
              </div>
            </div>
          </>
        )}

        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs font-medium text-gray-600" htmlFor={`${idBase}-op-${kind}`}>
              {t("lblPdfAnnotationOpacity")}
            </label>
            <span className="text-xs text-gray-600">{Math.round(value.opacity * 100)} %</span>
          </div>
          <input
            id={`${idBase}-op-${kind}`}
            type="range"
            min={0}
            max={1}
            step={0.01}
            className="w-full accent-blue-600"
            value={value.opacity}
            onChange={(e) => onPatch({ opacity: Math.min(1, Math.max(0, Number(e.target.value) || 0)) })}
          />
        </div>

        {showRectSizeFields ? (
          <>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600" htmlFor={`${idBase}-rw-${kind}`}>
                {t("lblPdfAnnotationPresetRectWidth")}
              </label>
              <div className="flex items-center gap-2">
                <U3140PresetNumericInput
                  id={`${idBase}-rw-${kind}`}
                  value={u3140RectDisplayWidthPx(value)}
                  min={U3140_RECT_PRESET_PX_MIN}
                  max={U3140_RECT_PRESET_PX_MAX}
                  emptyFallback={U3140_RECT_DEFAULT_WIDTH_PX}
                  onValueChange={(p) =>
                    onPatch({
                      defaultWidthPx: u3140ClampRectPx(p),
                      defaultWidthNorm: undefined,
                    })
                  }
                />
                <span className="text-xs text-gray-500">px</span>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600" htmlFor={`${idBase}-rh-${kind}`}>
                {t("lblPdfAnnotationPresetRectHeight")}
              </label>
              <div className="flex items-center gap-2">
                <U3140PresetNumericInput
                  id={`${idBase}-rh-${kind}`}
                  value={u3140RectDisplayHeightPx(value)}
                  min={U3140_RECT_PRESET_PX_MIN}
                  max={U3140_RECT_PRESET_PX_MAX}
                  emptyFallback={U3140_RECT_DEFAULT_HEIGHT_PX}
                  onValueChange={(p) =>
                    onPatch({
                      defaultHeightPx: u3140ClampRectPx(p),
                      defaultHeightNorm: undefined,
                    })
                  }
                />
                <span className="text-xs text-gray-500">px</span>
              </div>
            </div>
          </>
        ) : null}

        {showTextFields ? (
          <>
            <U3142StrokeColorRow
              idPrefix={`${idBase}-tx-${kind}`}
              label={t("lblPdfAnnotationTextColor")}
              value={value.textColor}
              onChange={(v) => onPatch({ textColor: v })}
            />
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-600" htmlFor={`${idBase}-fs-${kind}`}>
                {t("lblPdfAnnotationFontSize")}
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <U3140PresetNumericInput
                  id={`${idBase}-fs-${kind}`}
                  value={value.fontSizePts}
                  min={6}
                  max={120}
                  emptyFallback={6}
                  onValueChange={(n) => onPatch({ fontSizePts: n })}
                />
                <span className="text-xs text-gray-500">px</span>
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-gray-600 block">{t("lblPdfAnnotationTextAlign")}</span>
              <div className="flex flex-wrap gap-2" role="group" aria-label={t("lblPdfAnnotationAlignGroupHorizontal")}>
                {(
                  [
                    ["left", t("lblPdfAnnotationAlignLeft")],
                    ["center", t("lblPdfAnnotationAlignCenter")],
                    ["right", t("lblPdfAnnotationAlignRight")],
                  ] as const
                ).map(([align, label]) => (
                  <label key={align} className="inline-flex items-center gap-1.5 text-sm text-gray-800">
                    <input
                      type="radio"
                      name={`${idBase}-ta-${kind}`}
                      className="border-gray-300"
                      checked={value.textAlign === align}
                      onChange={() => onPatch({ textAlign: align })}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600" htmlFor={`${idBase}-tw-${kind}`}>
                {t("lblPdfAnnotationPresetTextBoxWidth")}
              </label>
              <div className="flex items-center gap-2">
                <U3140PresetNumericInput
                  id={`${idBase}-tw-${kind}`}
                  value={Math.round(value.textBoxWidthNorm * 100)}
                  min={4}
                  max={100}
                  emptyFallback={4}
                  onValueChange={(p) => onPatch({ textBoxWidthNorm: u3142ClampWidthNorm(p / 100) })}
                />
                <span className="text-xs text-gray-500">% {t("lblPdfAnnotationPresetOfViewport")}</span>
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-gray-600 block">
                {t("lblPdfAnnotationTextValign")}
              </span>
              <div className="flex flex-wrap gap-2" role="group">
                {(
                  [
                    ["top", t("lblPdfAnnotationValignTop")],
                    ["middle", t("lblPdfAnnotationValignMiddle")],
                    ["bottom", t("lblPdfAnnotationValignBottom")],
                  ] as const
                ).map(([v, label]) => (
                  <label key={v} className="inline-flex items-center gap-1.5 text-sm text-gray-800">
                    <input
                      type="radio"
                      name={`${idBase}-v-${kind}`}
                      className="border-gray-300"
                      checked={value.textBoxVerticalAlign === v}
                      onChange={() => onPatch({ textBoxVerticalAlign: v })}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

const KIND_TITLE_KEYS: Record<U3140PdfAnnotationPresetShapeKind, string> = {
  rect: "btnPdfAnnotationAddRect",
  line: "btnPdfAnnotationAddLine",
  arrow: "btnPdfAnnotationAddArrow",
  text: "btnPdfAnnotationAddText",
};

export interface U3140PdfAnnotationToolbarPresetStyleFormProps {
  value: U3140PdfAnnotationToolbarPresetStyles;
  onChange: Dispatch<SetStateAction<U3140PdfAnnotationToolbarPresetStyles>>;
  disabled?: boolean;
  i18nNamespace?: string;
}

/**
 * Grille `gap-2` : même espacement horizontal (entre colonnes) et vertical (pile rect / ligne / flèche) en col.2.
 */
export function U3140PdfAnnotationToolbarPresetStyleForm({
  value,
  onChange,
  disabled = false,
  i18nNamespace,
}: U3140PdfAnnotationToolbarPresetStyleFormProps) {
  const ns = i18nNamespace ?? "u3140";
  const { t } = useTranslation(ns);
  const patch = useCallback(
    (kind: U3140PdfAnnotationPresetShapeKind, p: Partial<U3140PdfAnnotationToolbarStylePreset>) => {
      onChange((prev) => ({ ...prev, [kind]: { ...prev[kind], ...p } }));
    },
    [onChange]
  );

  return (
    <div className={disabled ? "pointer-events-none opacity-60" : undefined} aria-hidden={disabled}>
      <div className={U3142_SHAPES_MENU_POPOVER_CLASS}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t("lblPdfAnnotationToolbarPresetFormSectionTitle")}
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:items-start">
          <div className="min-w-0 sm:col-start-1 sm:row-start-1">
            <U3142PresetFieldset
              kind="text"
              value={value.text}
              onPatch={(p) => patch("text", p)}
              sectionTitle={t(KIND_TITLE_KEYS.text)}
              i18nNamespace={ns}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-2 sm:col-start-2 sm:row-start-1">
            <U3142PresetFieldset
              kind="rect"
              value={value.rect}
              onPatch={(p) => patch("rect", p)}
              sectionTitle={t(KIND_TITLE_KEYS.rect)}
              i18nNamespace={ns}
            />
            <U3142PresetFieldset
              kind="line"
              value={value.line}
              onPatch={(p) => patch("line", p)}
              sectionTitle={t(KIND_TITLE_KEYS.line)}
              i18nNamespace={ns}
            />
            <U3142PresetFieldset
              kind="arrow"
              value={value.arrow}
              onPatch={(p) => patch("arrow", p)}
              sectionTitle={t(KIND_TITLE_KEYS.arrow)}
              i18nNamespace={ns}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
